"use client";

import Link from "next/link";
import Image from "next/image";
import { useRouter } from "next/navigation";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";

type ServiceItem = {
  id: string;
  title: string;
  desc: string;
  detailLink: string;
  priceLink: string;
  image: string;
  badge: string;
};

export default function ServicesPageContent({ services }: { services: ServiceItem[] }) {
  const router = useRouter();

  const handleCardNavigate = (href: string) => {
    router.push(href);
  };

  return (
    <>
      <section className="py-24 px-6 bg-secondary border-t border-white/5">
        <div className="max-w-7xl mx-auto flex flex-col gap-8">
          {services.map((service, index) => (
            <motion.article
              key={service.id}
              id={service.id === "ppf" ? "teenus-ppf" : service.id === "keraamika" ? "teenus-keraamika" : "teenus-toonimine"}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              whileHover={{ scale: 1.02, y: -4, boxShadow: "0 24px 60px rgba(0,0,0,0.18)" }}
              role="button"
              tabIndex={0}
              onClick={() => handleCardNavigate(service.detailLink)}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  handleCardNavigate(service.detailLink);
                }
              }}
              className="group cursor-pointer rounded-sm border border-white/10 bg-primary/95 overflow-hidden transition-colors duration-300 hover:border-accent/30"
            >
              <div className="flex flex-col lg:flex-row">
                <div className="w-full lg:w-[38%] relative aspect-[16/10] lg:aspect-auto bg-primary/20 overflow-hidden">
                  <Image
                    src={service.image}
                    alt={`${service.title} PPF Garage Tallinnas`}
                    fill
                    quality={80}
                    sizes="(min-width: 1024px) 33vw, 100vw"
                    className="object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-primary/10 group-hover:bg-transparent transition-colors duration-500"></div>
                </div>

                <div className="w-full lg:w-[62%] p-8 md:p-10 lg:p-12 flex flex-col justify-center relative">
                  <span className="inline-flex self-start mb-5 rounded-full border border-accent/30 bg-accent/10 px-3 py-1 text-[12px] font-heading font-semibold uppercase tracking-[0.2em] text-accent">
                    {service.badge}
                  </span>
                  <h2 className="font-heading font-bold text-[28px] md:text-[32px] text-text-main mb-4">
                    {service.title}
                  </h2>
                  <p className="font-body text-[16px] text-text-muted leading-relaxed max-w-xl mb-8">
                    {service.desc}
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3 mt-auto">
                    <Link
                      href={service.priceLink}
                      onClick={(event) => event.stopPropagation()}
                      className="inline-flex items-center justify-center rounded-sm bg-accent px-5 py-3 font-heading font-bold text-primary transition-colors duration-200 hover:bg-[#d8b884]"
                    >
                      Vaata hinda
                    </Link>
                    <Link
                      href={service.detailLink}
                      onClick={(event) => event.stopPropagation()}
                      className="inline-flex items-center justify-center rounded-sm border border-white/10 px-5 py-3 font-body font-medium text-text-main transition-colors duration-200 hover:border-accent/30 hover:text-accent"
                    >
                      Kirjeldus
                    </Link>
                  </div>
                </div>
              </div>
            </motion.article>
          ))}
        </div>
      </section>

      <section className="py-24 px-6 bg-primary border-t border-white/5 text-center">
        <div className="max-w-3xl mx-auto">
          <span className="text-accent uppercase text-[12px] tracking-[0.15em] font-bold font-body block mb-4">
            Kvaliteet ilma kompromissideta
          </span>
          <h2 className="font-heading font-bold text-[32px] md:text-[40px] text-text-main mb-6">
            Miks me kasutame CarPro tooteid?
          </h2>
          <p className="font-body text-[16px] text-text-muted leading-relaxed">
            CarPro on ülemaailmne liider nanotehnoloogiliste autohooldus- ja kaitsetoodete arendamisel.
            Meie valik langes CarPro kasuks, kuna nende tooted pakuvad konkurentsitult pikimat püsivust,
            kõrgeimat vastupidavust karmidele ilmastikutingimustele ja sügavaimat läiget.
            See on valik neile, kes soovivad oma sõidukile parimat.
          </p>
          <Link href="/tood" className="mt-8 inline-flex items-center gap-2 text-accent font-body font-medium hover:underline underline-offset-4 transition-all">
            Vaata meie töid <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      <section className="bg-gradient-to-r from-secondary to-primary border-y border-accent/20 py-24 px-6 text-center">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-heading font-bold text-[36px] text-text-main mb-6 leading-tight">
            Hinnakiri ja täpne kalkulatsioon
          </h2>
          <p className="font-body text-[18px] text-text-muted mb-10">
            Täpne hind sõltub auto mudelist ja seisukorrast — arvutame koos Teiega.
          </p>
          <Link href="/hinnakiri" className="inline-flex bg-accent text-primary font-heading font-bold px-8 py-4 rounded-sm hover:bg-accent/90 transition-colors duration-200">
            Hinnakiri ja täpne kalkulatsioon
          </Link>
        </div>
      </section>
    </>
  );
}
